// The fill handle: drag the corner of a selection down or right and the cells it
// passes over take the selection's contents, as in a spreadsheet. Pure, so the
// gesture in `useTableFillDrag` only has to say where the pointer is.
import {normalizeRange} from './table-ops';
import type {CellRange, TableCellData, TableConfig, TableRowData} from './table-types';

/** Whether a cell is part of a merge, as the anchor or as a covered cell. */
function isMerged(cell: TableCellData): boolean {
  return !!cell.hidden || (cell.rowSpan ?? 1) > 1 || (cell.colSpan ?? 1) > 1;
}

/**
 * Whether a selection can be filled from: every cell in it present and unmerged.
 *
 * Copying half a merge has no shape to land in, so a selection holding one is
 * not offered the handle at all, rather than filling some of its cells and
 * quietly skipping the rest. With no merge in it, the selection's bottom-right
 * corner is always the cell at `endRow`, `endCol`.
 */
export function canFillFrom(rows: readonly TableRowData[], range: CellRange): boolean {
  const n = normalizeRange(range);
  for (let ri = n.startRow; ri <= n.endRow; ri++) {
    for (let ci = n.startCol; ci <= n.endCol; ci++) {
      const cell = rows[ri]?.cells[ci];
      if (!cell || isMerged(cell)) return false;
    }
  }
  return true;
}

/**
 * The range a fill covers with the pointer over cell (`row`, `col`), or `null`
 * for no fill.
 *
 * `source` is the selection being filled from, normalised. The fill runs down
 * or right only, along whichever axis the pointer has gone further past the
 * selection — so a slightly diagonal drag still fills one way, as a spreadsheet
 * does. Anywhere else, the selection itself included, is no fill.
 */
export function fillTarget(source: CellRange, row: number, col: number): CellRange | null {
  const down = row - source.endRow;
  const right = col - source.endCol;
  if (down > 0 && down >= right) return {...source, endRow: row};
  if (right > 0) return {...source, endCol: col};
  return null;
}

/**
 * Copy the `source` range across `target`, repeating it when the target is
 * longer — two rows filled down six rows lands the pair three times.
 *
 * `source` must pass `canFillFrom`. The whole cell is copied — its text and the
 * styles on part of it, its elements, its formatting — all but its `id`, so a
 * field a cell gains later is copied without this changing. Each target keeps
 * its own `id`, because a filled
 * answer is keyed by cell id (see `cellFillKey`), so a copy is a new slot rather
 * than a second view of the old one. A merged target is left alone, for the
 * same reason a merged source is refused.
 */
export function tableFillRange(
  cfg: TableConfig,
  source: CellRange,
  target: CellRange,
): TableConfig {
  const height = source.endRow - source.startRow + 1;
  const width = source.endCol - source.startCol + 1;
  const rows = cfg.rows.map((row, ri) => {
    if (ri < target.startRow || ri > target.endRow) return row;
    const fromRow = cfg.rows[source.startRow + ((ri - source.startRow) % height)];
    return {
      ...row,
      cells: row.cells.map((cell, ci) => {
        if (ci < target.startCol || ci > target.endCol) return cell;
        if (ri <= source.endRow && ci <= source.endCol) return cell;
        const from = fromRow?.cells[source.startCol + ((ci - source.startCol) % width)];
        if (!from || isMerged(cell)) return cell;
        return {...from, id: cell.id};
      }),
    };
  });
  return {...cfg, rows};
}
